<template>
    <div v-if="playlist" class="listener-playlist">
        <div class="listener-playlist__backdrop" :style="backdropStyle"></div>
        <div class="listener-playlist__glow"></div>

        <div class="listener-playlist__shell">
            <div class="listener-playlist__topbar">
                <button class="listener-playlist__icon-button" @click="goBack">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M15 18 9 12l6-6"></path>
                    </svg>
                </button>
                <div class="listener-playlist__heading">
                    <div class="listener-playlist__eyebrow">{{ $tt('播放列表详情') }}</div>
                    <div class="listener-playlist__caption">{{ playlist.owner || $tt('我的歌单') }}</div>
                </div>
            </div>

            <div class="listener-playlist__display-switch-wrap">
                <div class="listener-playlist__display-switch">
                    <button
                        :class="['listener-playlist__display-pill', !showOverviewPanel ? 'is-active' : '']"
                        @click="setDisplayMode(false)"
                    >
                        {{ $tt('封面') }}
                    </button>
                    <button
                        :class="['listener-playlist__display-pill', showOverviewPanel ? 'is-active' : '']"
                        @click="setDisplayMode(true)"
                    >
                        {{ $tt('概览') }}
                    </button>
                </div>
            </div>

            <section class="listener-playlist__hero">
                <div class="listener-playlist__display-stage">
                    <div v-if="!showOverviewPanel" class="listener-playlist__art-stage">
                        <div class="listener-playlist__vinyl"></div>
                        <div class="listener-playlist__art-card">
                            <img
                                :src="playlist.image || '/static/dist/img/album_null.jpg'"
                                :alt="playlist.name"
                                class="listener-playlist__art-image"
                            >
                        </div>
                    </div>
                    <div v-else class="listener-playlist__overview-stage">
                        <div class="listener-playlist__overview-card">
                            <div class="listener-playlist__overview-label">{{ $tt('简介') }}</div>
                            <p class="listener-playlist__overview-text">
                                {{ playlist.comment || $tt('暂无简介') }}
                            </p>
                        </div>

                        <div class="listener-playlist__overview-card">
                            <div class="listener-playlist__overview-label">{{ $tt('歌单信息') }}</div>
                            <div class="listener-playlist__info-list">
                                <div class="listener-playlist__info-row">
                                    <span>{{ $tt('曲目') }}</span>
                                    <strong>{{ trackCount }}</strong>
                                </div>
                                <div class="listener-playlist__info-row">
                                    <span>{{ $tt('总时长') }}</span>
                                    <strong>{{ totalDuration }}</strong>
                                </div>
                                <div class="listener-playlist__info-row">
                                    <span>{{ $tt('创建者') }}</span>
                                    <strong>{{ playlist.owner || '--' }}</strong>
                                </div>
                                <div class="listener-playlist__info-row">
                                    <span>{{ $tt('创建时间') }}</span>
                                    <strong>{{ formatDate(playlist.createdAt) }}</strong>
                                </div>
                                <div class="listener-playlist__info-row">
                                    <span>{{ $tt('更新时间') }}</span>
                                    <strong>{{ formatDate(playlist.updatedAt) }}</strong>
                                </div>
                                <div class="listener-playlist__info-row">
                                    <span>{{ $tt('状态') }}</span>
                                    <strong>{{ statusText }}</strong>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                <div class="listener-playlist__meta">
                    <div class="listener-playlist__meta-kicker">{{ $tt('Playlist') }}</div>
                    <h1 class="listener-playlist__title">{{ playlist.name }}</h1>
                    <p v-if="playlist.comment" class="listener-playlist__summary" @click="openCommentModal">
                        {{ commentPreview }}
                    </p>

                    <div class="listener-playlist__actions">
                        <button class="listener-playlist__play-button" @click="playNow(playlist.id)">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                            </svg>
                            <span>{{ $tt('播放') }}</span>
                        </button>
                        <button class="listener-playlist__action-button" @click="shuffleNow">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17 3h4v4h-2V6.41l-4.59 4.58-1.41-1.41L17.59 5H17V3zM3 6h2.59l5.29 5.29-1.41 1.41L4.41 8H3V6zm10.53 5.47 1.41 1.41L12.41 15.4l1.06 1.06-1.41 1.41L9.59 15.4 3 22v-2.83l5.17-5.17L3 8.83V6l10.53 10.47zM17 17h.59l-4.59-4.58 1.41-1.41L19 15.59V15h2v4h-4v-2z"></path>
                            </svg>
                            <span>{{ $tt('随机') }}</span>
                        </button>
                        <button class="listener-playlist__action-button" @click="setNextInQueue">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 5h10v2H4zm0 6h10v2H4zm0 6h10v2H4zm12-7V6l6 5-6 5v-4z"></path>
                            </svg>
                            <span>{{ $tt('下一首') }}</span>
                        </button>
                        <button class="listener-playlist__action-button" @click="addToQueue">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 6h12v2H4zm0 5h12v2H4zm0 5h8v2H4zm14-5V8h2v3h3v2h-3v3h-2v-3h-3v-2z"></path>
                            </svg>
                            <span>{{ $tt('队列') }}</span>
                        </button>
                    </div>
                </div>
            </section>

            <section class="listener-playlist__panel listener-playlist__panel--tracks">
                <div class="listener-playlist__panel-header">
                    <div>
                        <div class="listener-playlist__panel-eyebrow">{{ $tt('歌曲列表') }}</div>
                        <h2 class="listener-playlist__panel-title">{{ $tt('{count} 首歌曲', { count: trackCount }) }}</h2>
                    </div>
                </div>
                <ListenerTrackList
                    :tracks="playlist.tracks"
                    index-field="index"
                    show-image
                    @remove-track="removeTrackFromPlaylist"
                />
            </section>
        </div>

        <dialog ref="playlistCommentModal" class="modal">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('歌单简介:') }}{{ playlist.name }}</h3>
                <div class="py-4 whitespace-pre-wrap break-words text-base-content/80">
                    {{ playlist.comment || $tt('暂无简介') }}
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>close</button>
            </form>
        </dialog>

        <PlaylistShareModal ref="shareModal" :playlist-id="id" />
    </div>
</template>

<script>
import ListenerTrackList from '@/views/library/track/ListenerTrackList.vue'
import PlaylistShareModal from '@/components/library/PlaylistShareModal.vue'

export default {
    name: 'ListenerPlaylistDetails',
    components: {
        ListenerTrackList,
        PlaylistShareModal
    },
    data() {
        return {
            playlist: null,
            id: this.$route.params.id,
            showOverviewPanel: false
        }
    },
    computed: {
        trackCount() {
            return this.playlist?.tracks?.length || 0
        },
        totalDuration() {
            const total = (this.playlist?.tracks || []).reduce((sum, item) => sum + (item.duration || 0), 0)
            return this.formatDuration(total)
        },
        commentPreview() {
            if (!this.playlist?.comment) return ''
            return this.playlist.comment.length > 120 ? `${this.playlist.comment.slice(0, 120)}...` : this.playlist.comment
        },
        backdropStyle() {
            return {
                backgroundImage: `url('${this.playlist?.image || '/static/dist/img/album_null.jpg'}')`
            }
        },
        statusText() {
            if (!this.playlist) {
                return '--'
            }
            if (this.playlist.privacyLevel === 'exclude') {
                return this.$tt('过滤列表')
            }
            if (this.playlist.isDynamic) {
                return this.$tt('动态歌单')
            }
            return this.$tt('普通歌单')
        }
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            handler(value) {
                this.loadPlaylist(value)
            }
        }
    },
    methods: {
        async loadPlaylist(id) {
            this.id = id
            this.playlist = await this.$api.subsonic.getPlaylist(id)
            this.showOverviewPanel = false
        },
        async playNow(id) {
            if (this.playlist && String(id) === String(this.playlist.id)) {
                return this.$store.dispatch('playTrackList', {
                    tracks: this.playlist.tracks
                })
            }
            const playlist = await this.$api.subsonic.getPlaylist(id)
            return this.$store.dispatch('playTrackList', {
                tracks: playlist.tracks
            })
        },
        shuffleNow() {
            return this.$store.dispatch('player/shuffleNow', {
                tracks: this.playlist && this.playlist.tracks ? this.playlist.tracks : []
            })
        },
        setNextInQueue() {
            if (this.playlist) {
                return this.$store.dispatch('player/setNextInQueue', this.playlist.tracks)
            }
        },
        addToQueue() {
            if (this.playlist) {
                return this.$store.dispatch('player/addToQueue', this.playlist.tracks)
            }
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back()
                return
            }
            this.$router.push({ path: '/playlists/recently-added/' })
        },
        setDisplayMode(isOverview) {
            this.showOverviewPanel = isOverview
        },
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const totalSeconds = Math.floor(value)
            const hours = Math.floor(totalSeconds / 3600)
            const minutes = Math.floor((totalSeconds % 3600) / 60)
            const seconds = totalSeconds % 60
            if (hours > 0) {
                return `${hours}:${`${minutes}`.padStart(2, '0')}:${`${seconds}`.padStart(2, '0')}`
            }
            return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
        },
        formatDate(value) {
            if (!value) return '--'
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) {
                return value
            }
            const year = date.getFullYear()
            const month = `${date.getMonth() + 1}`.padStart(2, '0')
            const day = `${date.getDate()}`.padStart(2, '0')
            return `${year}-${month}-${day}`
        },
        openCommentModal() {
            if (this.$refs.playlistCommentModal) {
                this.$refs.playlistCommentModal.showModal()
            }
        },
        handleShare() {
            this.$refs.shareModal.show()
        },
        exportPlaylist(id) {
            this.$api.subsonic.exportPlaylist(id).then((res) => {
                window.open(res.url, '_blank')
            })
        },
        updatePlaylistProps(id, props) {
            this.$api.subsonic.updatePlaylistProps(id, props).then(() => {
                this.$myMsg.notify({
                    title: this.$tt('更新成功！'),
                    type: 'success',
                    time: 2000,
                })
                this.loadPlaylist(id)
            })
        },
        duplicatePlaylist(id) {
            this.$api.subsonic.duplicatePlaylist(id).then(() => {
                this.$myMsg.notify({
                    title: this.$tt('去重成功！'),
                    type: 'success',
                    time: 2000,
                })
                this.loadPlaylist(id)
            })
        },
        removeTrackFromPlaylist(index) {
            if (!this.playlist || !this.playlist.tracks) {
                return
            }
            this.playlist.tracks.splice(index, 1)
        }
    }
}
</script>

<style scoped>
.listener-playlist {
    --display-stage-size: min(19rem, 78vw, 34dvh);
    position: relative;
    height: calc(100dvh - 12.25rem);
    box-sizing: border-box;
    padding: 0.85rem 0.9rem 2rem;
    color: #0f172a;
    background:
        radial-gradient(circle at top, rgba(255, 255, 255, 0.92), rgba(244, 247, 251, 0.94) 48%, rgba(241, 245, 249, 0.98) 100%);
    overflow-y: auto;
    overflow-x: hidden;
}

.listener-playlist__backdrop {
    position: absolute;
    inset: 0;
    background-position: center top;
    background-size: cover;
    filter: blur(44px);
    opacity: 0.18;
    transform: scale(1.08);
    pointer-events: none;
}

.listener-playlist__glow {
    position: absolute;
    top: -4rem;
    left: 50%;
    width: 22rem;
    height: 22rem;
    border-radius: 999px;
    transform: translateX(-50%);
    background: radial-gradient(circle, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0));
    pointer-events: none;
}

.listener-playlist__shell {
    position: relative;
    z-index: 1;
}

.listener-playlist__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.listener-playlist__heading {
    flex: 1;
    text-align: center;
    min-width: 0;
}

.listener-playlist__eyebrow {
    font-size: 0.78rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-playlist__caption {
    margin-top: 0.12rem;
    font-size: 0.84rem;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-playlist__icon-button {
    width: 2.8rem;
    height: 2.8rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.84);
    color: #111827;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.16);
    flex-shrink: 0;
}

.listener-playlist__icon-button svg {
    width: 1.2rem;
    height: 1.2rem;
}

.listener-playlist__hero {
    display: flex;
    flex-direction: column;
}

.listener-playlist__display-stage {
    height: var(--display-stage-size);
    margin-top: 1.25rem;
    margin-bottom: 1.45rem;
    display: flex;
    align-items: stretch;
}

.listener-playlist__art-stage {
    position: relative;
    margin: 0 auto;
    width: var(--display-stage-size);
    height: var(--display-stage-size);
    align-self: center;
}

.listener-playlist__overview-stage {
    width: 100%;
    height: 100%;
    display: grid;
    align-content: start;
    gap: 0.9rem;
    overflow-y: auto;
    padding-right: 0.1rem;
}

.listener-playlist__vinyl {
    position: absolute;
    right: -1.5rem;
    top: 1.7rem;
    width: 72%;
    height: 72%;
    border-radius: 999px;
    background:
        radial-gradient(circle at center, #0f172a 0 10%, #111827 10% 15%, #020617 15% 52%, #111827 52% 60%, #020617 60% 100%);
    box-shadow: 0 22px 50px rgba(15, 23, 42, 0.22);
}

.listener-playlist__art-card {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
    padding: 0.8rem;
    border-radius: 2rem;
    background: linear-gradient(180deg, rgba(255,255,255,0.72), rgba(255,255,255,0.36));
    backdrop-filter: blur(20px);
    box-shadow: 0 28px 70px rgba(59, 130, 246, 0.18);
}

.listener-playlist__art-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 1.45rem;
}

.listener-playlist__meta-kicker {
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #94a3b8;
}

.listener-playlist__title {
    margin-top: 0.5rem;
    font-size: 2rem;
    line-height: 1.05;
    font-weight: 800;
    letter-spacing: -0.04em;
    color: #111827;
    word-break: break-word;
}

.listener-playlist__owner {
    margin-top: 0.65rem;
    font-size: 1rem;
    color: #64748b;
}

.listener-playlist__summary {
    margin-top: 0.95rem;
    font-size: 0.92rem;
    line-height: 1.75;
    color: #64748b;
    cursor: pointer;
}

.listener-playlist__chips {
    margin-top: 1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
}

.listener-playlist__chip {
    min-height: 2.1rem;
    padding: 0.45rem 0.9rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.82);
    color: #334155;
    font-size: 0.82rem;
    font-weight: 700;
    box-shadow: 0 8px 22px rgba(148, 163, 184, 0.1);
}

.listener-playlist__chip--primary {
    background: #111827;
    color: #fff;
}

.listener-playlist__actions {
    margin-top: 1.35rem;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.7rem;
}

.listener-playlist__play-button,
.listener-playlist__action-button {
    min-height: 3.45rem;
    border: 0;
    border-radius: 1.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    font-weight: 700;
}

.listener-playlist__play-button {
    background: #111827;
    color: #fff;
    box-shadow: 0 18px 30px rgba(15, 23, 42, 0.24);
}

.listener-playlist__action-button {
    background: rgba(255, 255, 255, 0.82);
    color: #0f172a;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
}

.listener-playlist__play-button svg,
.listener-playlist__action-button svg {
    width: 1.1rem;
    height: 1.1rem;
}

.listener-playlist__display-switch-wrap {
    margin: 1.15rem auto 0;
    width: 100%;
    display: flex;
    justify-content: center;
}

.listener-playlist__display-switch {
    padding: 0.28rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
    width: fit-content;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.28rem;
}

.listener-playlist__display-pill {
    min-width: 4.7rem;
    height: 2.2rem;
    padding: 0 1rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 700;
}

.listener-playlist__display-pill.is-active {
    background: #111827;
    color: #fff;
}

.listener-playlist__panel {
    margin-top: 1.2rem;
}

.listener-playlist__panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.95rem;
}

.listener-playlist__panel-eyebrow {
    font-size: 0.74rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-playlist__panel-title {
    margin-top: 0.22rem;
    font-size: 1.3rem;
    line-height: 1.2;
    font-weight: 800;
    color: #111827;
}

.listener-playlist__overview-card {
    padding: 1rem;
    border-radius: 1.3rem;
    background: rgba(255, 255, 255, 0.76);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.1);
    backdrop-filter: blur(14px);
}

.listener-playlist__overview-label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.12em;
}

.listener-playlist__overview-text {
    margin-top: 0.75rem;
    color: #475569;
    line-height: 1.8;
    white-space: pre-wrap;
    word-break: break-word;
}

.listener-playlist__info-list {
    margin-top: 0.75rem;
    display: grid;
    gap: 0.8rem;
}

.listener-playlist__info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    font-size: 0.94rem;
}

.listener-playlist__info-row span {
    color: #94a3b8;
}

.listener-playlist__info-row strong {
    color: #0f172a;
    font-weight: 700;
    text-align: right;
}

.listener-playlist__tool-actions {
    margin-top: 0.75rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.7rem;
}

.listener-playlist__tool-button {
    min-height: 3rem;
    padding: 0.65rem 1rem;
    border: 0;
    border-radius: 1.15rem;
    background: rgba(255, 255, 255, 0.82);
    color: #0f172a;
    font-size: 0.86rem;
    font-weight: 700;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
}

.listener-playlist__panel--tracks {
    padding-bottom: 0.6rem;
}

@media (max-width: 380px) {
    .listener-playlist {
        padding-left: 0.75rem;
        padding-right: 0.75rem;
    }

    .listener-playlist__actions {
        grid-template-columns: minmax(0, 1fr);
    }

    .listener-playlist__display-pill {
        min-width: 4.3rem;
        padding-left: 0.85rem;
        padding-right: 0.85rem;
    }
}
</style>
